import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faFileContract, faSearch, faDownload, faEye, 
    faCheckCircle, faExclamationTriangle, faInfoCircle, faGavel
} from '@fortawesome/free-solid-svg-icons';
import DashboardHeader from '../../layouts/dashboard_header';
import SideMenu from '../../layouts/dashboard_menu';
import DashboardFooter from '../../layouts/dashboard_footer';
import localForage from 'localforage';

interface TermsSection {
    id: string;
    title: string;
    content: string;
    lastUpdated: string;
}

const TermsConditions: React.FC = () => {
    const [sections, setSections] = useState<TermsSection[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [selectedSection, setSelectedSection] = useState<string>('overview');
    const [clinicName, setClinicName] = useState<string>('Facility');

    useEffect(() => {
        document.title = "Terms & Conditions - Uzima Healthcare";
        loadClinicData();
        loadTermsConditions();
    }, []);

    const loadClinicData = async () => {
        try {
            const storedClinicName = await localForage.getItem('clinic_name');
            if (storedClinicName) {
                setClinicName(storedClinicName as string);
            }
        } catch (error) {
            console.error('Error loading clinic data:', error);
        }
    };

    const loadTermsConditions = () => {
        // Mock terms and conditions data - in real app, fetch from API
        const mockSections: TermsSection[] = [
            {
                id: 'overview',
                title: 'Overview and Acceptance',
                content: `Welcome to Uzima Healthcare. These Terms and Conditions ("Terms") govern your use of our healthcare platform and services. By accessing or using our services, you agree to be bound by these Terms.

If you do not agree to these Terms, please do not use our services. We reserve the right to modify these Terms at any time, and your continued use of our services constitutes acceptance of any changes.

These Terms apply to all users of our platform, including patients, healthcare professionals, institutional administrators, and partners.`,
                lastUpdated: '2025-09-05'
            },
            {
                id: 'services',
                title: 'Description of Services',
                content: `Uzima Healthcare provides a comprehensive digital healthcare platform that includes:

Healthcare Services:
- Telehealth consultations and virtual appointments
- Electronic health records management
- Patient portal and health information access
- IoT device integration for health monitoring
- Prescription management and medication tracking

Platform Features:
- Secure messaging and communication tools
- Appointment scheduling and management
- Billing and payment processing
- Data analytics and reporting
- Integration with third-party healthcare systems

We strive to provide reliable, secure, and user-friendly healthcare services. However, our platform is not a substitute for emergency medical care, and users should seek immediate medical attention for emergencies.`,
                lastUpdated: '2025-09-05'
            },
            {
                id: 'user-accounts',
                title: 'User Accounts and Responsibilities',
                content: `Account Creation:
- You must provide accurate and complete information when creating an account
- You are responsible for maintaining the confidentiality of your account credentials
- You must notify us immediately of any unauthorized use of your account
- You are responsible for all activities that occur under your account

User Responsibilities:
- Use the platform in accordance with applicable laws and regulations
- Provide accurate and up-to-date health information
- Respect the privacy and confidentiality of other users
- Report any technical issues or security concerns promptly
- Comply with all platform policies and guidelines

Account Termination:
- We reserve the right to suspend or terminate accounts for violations of these Terms
- You may terminate your account at any time through your account settings
- Upon termination, your access to the platform will be revoked
- Certain information may be retained as required by law or for legitimate business purposes`,
                lastUpdated: '2025-09-05'
            },
            {
                id: 'healthcare-disclaimer',
                title: 'Healthcare Disclaimer',
                content: `Important Medical Disclaimer:

Not Emergency Care:
- Our platform is not intended for emergency medical situations
- In case of medical emergency, call emergency services immediately
- Do not rely on our platform for urgent or life-threatening conditions

Professional Medical Care:
- Our platform facilitates connections with healthcare professionals
- We do not provide medical advice, diagnosis, or treatment
- All medical decisions should be made in consultation with qualified healthcare providers
- Healthcare professionals using our platform are responsible for their own medical practices

Limitations:
- We cannot guarantee the availability of healthcare providers
- Technical issues may affect the delivery of healthcare services
- Users are responsible for verifying the credentials of healthcare providers
- We are not liable for medical outcomes or treatment results`,
                lastUpdated: '2025-09-05'
            },
            {
                id: 'privacy-data',
                title: 'Privacy and Data Protection',
                content: `Data Collection and Use:
- We collect and process personal and health information as described in our Privacy Policy
- Your health information is protected under applicable privacy laws
- We implement appropriate security measures to protect your data
- We may use aggregated, anonymized data for research and improvement purposes

Data Sharing:
- We may share your information with healthcare providers involved in your care
- We may disclose information as required by law or legal process
- We do not sell your personal information to third parties
- We may share information with service providers who assist in platform operations

Your Rights:
- You have the right to access, correct, and delete your personal information
- You can control your privacy settings and communication preferences
- You may request copies of your health records
- You can withdraw consent for certain data processing activities`,
                lastUpdated: '2025-09-05'
            },
            {
                id: 'billing-payments',
                title: 'Billing and Payment Terms',
                content: `Payment Terms:
- Subscription fees are billed in advance on a monthly or annual basis
- Payment is due upon receipt of invoice
- We accept various payment methods including credit cards and bank transfers
- All fees are non-refundable except as required by law

Billing Disputes:
- You must notify us of any billing disputes within 30 days
- We will investigate and resolve disputes in good faith
- Continued use of services after dispute notification constitutes acceptance of charges
- We may suspend services for non-payment after appropriate notice

Price Changes:
- We may modify subscription fees with 30 days' notice
- Price changes will not affect current billing periods
- You may cancel your subscription if you do not agree to price changes
- Free trial periods and promotional pricing are subject to terms and conditions`,
                lastUpdated: '2025-09-05'
            },
            {
                id: 'intellectual-property',
                title: 'Intellectual Property Rights',
                content: `Platform Ownership:
- Uzima Healthcare owns all rights to the platform, software, and technology
- Our trademarks, logos, and branding are protected intellectual property
- Users may not copy, modify, or distribute our proprietary materials
- We grant users a limited, non-exclusive license to use the platform

User Content:
- Users retain ownership of their personal health information and data
- Users grant us necessary licenses to provide healthcare services
- Users are responsible for ensuring they have rights to any content they share
- We may use anonymized data for platform improvement and research

Third-Party Content:
- Our platform may include content from third-party sources
- We are not responsible for third-party content accuracy or availability
- Users must comply with third-party terms and conditions
- We may remove third-party content that violates our policies`,
                lastUpdated: '2025-09-05'
            },
            {
                id: 'limitations',
                title: 'Limitations of Liability',
                content: `Service Availability:
- We strive to maintain high service availability but cannot guarantee 100% uptime
- We are not liable for temporary service interruptions or technical issues
- Users should have backup plans for critical healthcare needs
- We may perform maintenance that temporarily affects service availability

Limitation of Damages:
- Our liability is limited to the amount paid for services in the 12 months preceding the claim
- We are not liable for indirect, incidental, or consequential damages
- We are not responsible for medical outcomes or treatment results
- Users assume responsibility for their use of the platform

Force Majeure:
- We are not liable for delays or failures due to circumstances beyond our control
- This includes natural disasters, government actions, and technical failures
- We will make reasonable efforts to minimize service disruptions
- Users should maintain appropriate backup healthcare arrangements`,
                lastUpdated: '2025-09-05'
            },
            {
                id: 'governing-law',
                title: 'Governing Law and Disputes',
                content: `Governing Law:
- These Terms are governed by the laws of the jurisdiction where Uzima Healthcare is incorporated
- Any disputes will be resolved in the courts of that jurisdiction
- Users consent to the jurisdiction of these courts
- Local laws may provide additional protections for users

Dispute Resolution:
- We encourage users to contact us first to resolve any disputes
- We will make good faith efforts to resolve disputes amicably
- If disputes cannot be resolved, they may be subject to binding arbitration
- Users may opt out of arbitration within 30 days of account creation

Compliance:
- Users must comply with all applicable local, state, and federal laws
- Healthcare professionals must maintain appropriate licenses and credentials
- Users are responsible for understanding and complying with relevant regulations
- We may suspend services for users who violate applicable laws`,
                lastUpdated: '2025-09-05'
            },
            {
                id: 'contact',
                title: 'Contact Information',
                content: `For questions about these Terms and Conditions, please contact us:

Legal Department:
Email: legal@uzimahealthcare.com
Phone: 1-800-UZIMA-LEGAL
Address: 123 Healthcare Drive, Medical City, MC 12345

General Inquiries:
Email: support@uzimahealthcare.com
Phone: 1-800-UZIMA-01

For legal notices and formal communications:
Email: notices@uzimahealthcare.com
Address: Legal Department, Uzima Healthcare, 123 Healthcare Drive, Medical City, MC 12345

We will respond to all inquiries within 30 days and work to resolve any concerns promptly and effectively.`,
                lastUpdated: '2025-09-05'
            }
        ];
        setSections(mockSections);
    };

    const filteredSections = sections.filter(section => 
        section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        section.content.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const currentSection = sections.find(section => section.id === selectedSection);

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="vertical-overlay" />
            
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        {/* Header */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <h2 className="fw-bold mb-1">
                                            <FontAwesomeIcon icon={faFileContract} className="me-2 text-primary" />
                                            Terms & Conditions
                                        </h2>
                                        <p className="text-muted mb-0">
                                            Terms of service and user agreements for {clinicName}
                                        </p>
                                    </div>
                                    <div className="text-end">
                                        <div className="badge bg-info fs-6 px-3 py-2">
                                            <FontAwesomeIcon icon={faGavel} className="me-1" />
                                            Legal Document
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Search */}
                        <div className="row mb-4">
                            <div className="col-lg-6">
                                <div className="search-box">
                                    <div className="position-relative">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search terms and conditions..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                        <FontAwesomeIcon 
                                            icon={faSearch} 
                                            className="search-icon position-absolute top-50 end-0 translate-middle-y me-3 text-muted"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-6 text-end">
                                <button className="btn btn-outline-primary">
                                    <FontAwesomeIcon icon={faDownload} className="me-2" />
                                    Download PDF
                                </button>
                            </div>
                        </div>

                        <div className="row">
                            {/* Navigation */}
                            <div className="col-lg-3">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-header bg-white border-0">
                                        <h6 className="card-title mb-0">Terms Sections</h6>
                                    </div>
                                    <div className="card-body p-0">
                                        <div className="list-group list-group-flush">
                                            {sections.map((section) => (
                                                <button
                                                    key={section.id}
                                                    className={`list-group-item list-group-item-action border-0 ${
                                                        selectedSection === section.id ? 'active' : ''
                                                    }`}
                                                    onClick={() => setSelectedSection(section.id)}
                                                >
                                                    <div className="d-flex align-items-center">
                                                        <FontAwesomeIcon 
                                                            icon={faEye} 
                                                            className="me-2 text-muted"
                                                        />
                                                        <span>{section.title}</span>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="col-lg-9">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-header bg-white border-0">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <h5 className="card-title mb-0">
                                                {currentSection?.title}
                                            </h5>
                                            <small className="text-muted">
                                                Last updated: {currentSection?.lastUpdated}
                                            </small>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <div className="terms-content">
                                            {currentSection ? (
                                                <div>
                                                    {currentSection.content.split('\n\n').map((paragraph, index) => (
                                                        <p key={index} className="mb-3">
                                                            {paragraph}
                                                        </p>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center py-5">
                                                    <FontAwesomeIcon icon={faFileContract} className="text-muted fs-1 mb-3" />
                                                    <h5 className="text-muted">Select a section to view</h5>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Key Points */}
                        <div className="row mt-4">
                            <div className="col-12">
                                <div className="card border-0 bg-light">
                                    <div className="card-body">
                                        <h5 className="mb-3">Important Terms Summary</h5>
                                        <div className="row">
                                            <div className="col-md-3 text-center mb-3">
                                                <FontAwesomeIcon icon={faCheckCircle} className="text-success fs-1 mb-2" />
                                                <h6>User Agreement</h6>
                                                <p className="text-muted small">By using our services, you agree to these terms</p>
                                            </div>
                                            <div className="col-md-3 text-center mb-3">
                                                <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning fs-1 mb-2" />
                                                <h6>Not Emergency Care</h6>
                                                <p className="text-muted small">Platform is not for emergency medical situations</p>
                                            </div>
                                            <div className="col-md-3 text-center mb-3">
                                                <FontAwesomeIcon icon={faInfoCircle} className="text-info fs-1 mb-2" />
                                                <h6>Data Protection</h6>
                                                <p className="text-muted small">Your privacy and data are protected</p>
                                            </div>
                                            <div className="col-md-3 text-center mb-3">
                                                <FontAwesomeIcon icon={faGavel} className="text-primary fs-1 mb-2" />
                                                <h6>Legal Compliance</h6>
                                                <p className="text-muted small">Must comply with applicable laws</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <DashboardFooter />
        </>
    );
};

export default TermsConditions;
